import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import logo from '@/assets/logo-header.webp';
import logoLight from '@/assets/logo-header-light.webp';
import { FerdyMopping } from '@/components/marketing/FerdyMopping';
import { PaymentLogo } from '@/components/marketing/PaymentBadge';
import { MarketingSeo } from '@/components/seo/MarketingSeo';
import '@/styles/marketing.css';

/**
 * Layout used exclusively for the SaaS marketing site (apex domain only).
 * Has its own nav + footer so it never collides with tenant chrome.
 * Every marketing page opens on a dark indigo hero, so the nav starts
 * transparent with light text and turns solid white once you scroll.
 */
export function MarketingLayout({
  children,
  tone = 'dark',
}: {
  children: ReactNode;
  /** 'light' for pages that don't open on the dark hero (e.g. signup). */
  tone?: 'dark' | 'light';
}) {
  return (
    <div className="marketing-site min-h-screen antialiased">
      <MarketingSeo />
      <MarketingNav alwaysSolid={tone === 'light'} />
      <main className={cn(tone === 'light' && 'pt-20')}>{children}</main>
      <MarketingFooter />
    </div>
  );
}

const navLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
  { to: '/demo', label: 'Live demo' },
];

function MarketingNav({ alwaysSolid = false }: { alwaysSolid?: boolean }) {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = alwaysSolid || scrolled || open;

  return (
    <header
      className={cn(
        'mk-nav fixed inset-x-0 top-0 z-50',
        solid
          ? 'bg-white/90 text-[hsl(var(--mk-ink))] shadow-[0_1px_0_hsl(var(--border)),0_10px_30px_-20px_hsl(var(--mk-ink)/0.35)] backdrop-blur-xl'
          : 'bg-transparent text-white'
      )}
    >
      <div className="container mx-auto flex h-20 items-center justify-between gap-6 px-4">
        <Link to="/" className="flex shrink-0 items-center" aria-label="The Polished Version home">
          <img
            src={solid ? logo : logoLight}
            alt="The Polished Version"
            className="h-[64px] w-[128px] object-contain"
            width={480}
            height={240}
            loading="eager"
            decoding="sync"
            fetchPriority="high"
          />
        </Link>

        <nav className="hidden items-center lg:flex" aria-label="Main">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => cn('mk-nav-link', isActive && 'active')}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/auth" className="mk-nav-link hidden sm:inline-flex">
            Sign in
          </Link>
          <Link to="/signup" className="mk-btn mk-btn-sun mk-btn-sm hidden sm:inline-flex">
            Start free <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full lg:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={open ? 'Close menu' : 'Open menu'}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-white px-4 pb-8 pt-4 lg:hidden">
          <nav className="flex flex-col" aria-label="Mobile">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  cn(
                    'rounded-2xl px-4 py-3 font-display text-lg',
                    isActive ? 'bg-[hsl(var(--mk-lilac))]' : 'hover:bg-[hsl(var(--mk-panel))]'
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-5 grid gap-3">
            <Link to="/signup" className="mk-btn mk-btn-sun">
              Start your free trial <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/auth" className="mk-btn mk-btn-ghost">
              Sign in
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

const FOOTER_COLUMNS = [
  {
    title: 'Product',
    links: [
      { to: '/features', label: 'Features' },
      { to: '/pricing', label: 'Pricing' },
      { to: '/demo', label: 'Live demo' },
      { to: '/signup', label: 'Free trial' },
    ],
  },
  {
    title: 'Company',
    links: [
      { to: '/about', label: 'About' },
      { to: '/faq', label: 'FAQs' },
      { to: '/sales-contact', label: 'Contact' },
      { to: '/auth', label: 'Sign in' },
    ],
  },
];

const footerHeading =
  'text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45 sm:text-xs sm:tracking-[0.16em]';

const LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'T&Cs' },
  { to: '/cookies', label: 'Cookies' },
];

function MarketingFooter() {
  return (
    <footer className="mk-ink-bg mk-on-dark relative overflow-hidden">
      <div className="container mx-auto px-4 pb-8 pt-16 md:pt-20">
        <div className="grid gap-12 lg:grid-cols-[1.25fr_2fr_auto] lg:items-start lg:gap-14">
          {/* Brand */}
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <img
                src={logoLight}
                alt="The Polished Version"
                className="h-14 w-28 -translate-x-[13.3%] object-contain object-left"
                width={480}
                height={240}
                loading="lazy"
                decoding="async"
              />
              <p className="mt-5 max-w-xs text-[15px] leading-relaxed text-white/65">
                Websites and booking software that help cleaning businesses get found, booked and paid.
              </p>
              <Link to="/signup" className="mk-btn mk-btn-sun mt-6">
                Start your free trial <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            {/* Ferdy sits beside the brand on phones and tablets. */}
            <div className="w-24 shrink-0 translate-x-[5%] sm:w-28 lg:hidden" aria-hidden>
              <FerdyMopping />
            </div>
          </div>

          {/* Link columns */}
          <nav aria-label="Footer" className="grid grid-cols-3 gap-4 sm:gap-8">
            {FOOTER_COLUMNS.map((col) => (
              <div key={col.title}>
                <p className={footerHeading}>{col.title}</p>
                <ul className="mt-4 space-y-3">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="text-[14px] text-white/80 transition-colors hover:text-[hsl(var(--mk-mint))] sm:text-[15px]">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {/* Third column: the services businesses connect (payments, text messages). */}
            <div aria-label="Integrations: Stripe, PayPal and Twilio">
              <p className={footerHeading}>Integrations</p>
              <div className="mt-4 flex flex-col items-start gap-2.5">
                <PaymentLogo brand="stripe" />
                <PaymentLogo brand="paypal" />
                <PaymentLogo brand="twilio" />
              </div>
            </div>
          </nav>

          {/* Same mopping Ferdy as the hero; the puddle edge lines up with the container edge. */}
          <div className="hidden w-36 translate-x-[5%] self-end lg:block" data-testid="footer-ferdy">
            <FerdyMopping />
          </div>
        </div>

        {/* Legal: one line, the links straight after the copyright. */}
        <div className="mt-12 flex flex-wrap items-center gap-x-[clamp(0.5rem,2.6vw,1.25rem)] gap-y-1 whitespace-nowrap border-t border-white/10 pt-6 text-[length:clamp(10.5px,3.3vw,12px)] text-white/50">
          <span>© {new Date().getFullYear()} The Polished Version</span>
          {LEGAL_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="transition-colors hover:text-white">
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}
